import { useContext, useState } from 'react';
import { ContentContext } from '../../contexts/ContentContext';
import useContent from '../../hooks/useContent';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import AdminTopbar from '../AdminTopbar';
import SaveBar from '../components/SaveBar';
import ConfirmModal from '../components/ConfirmModal';

export default function HowItWorksEditor() {
  const { content, updateSection } = useContext(ContentContext);
  const { draft, isDirty, saving, lastSaved, update, save, discard } =
    useContent(content.howItWorks);
  useUnsavedChanges(isDirty);

  const [deleteIdx, setDeleteIdx] = useState(null);

  const handleSave    = () => save(patch => updateSection('howItWorks', patch));
  const handleDiscard = () => discard(content.howItWorks);

  const steps = draft.steps || [];

  const updateStep = (i, patch) =>
    update({ steps: steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) });

  const addStep = () =>
    update({ steps: [...steps, { num: String(steps.length + 1).padStart(2, '0'), title: 'New Step', desc: '' }] });

  const removeStep = (i) =>
    update({ steps: steps.filter((_, idx) => idx !== i) });

  const moveStep = (from, to) => {
    const next = [...steps];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    update({ steps: next });
  };

  return (
    <div className="adm-page">
      <AdminTopbar title="How It Works" />
      <SaveBar isDirty={isDirty} saving={saving} lastSaved={lastSaved}
               onSave={handleSave} onDiscard={handleDiscard} />

      {deleteIdx !== null && (
        <ConfirmModal
          message={`Delete step "${steps[deleteIdx]?.title}"?`}
          onConfirm={() => { removeStep(deleteIdx); setDeleteIdx(null); }}
          onCancel={() => setDeleteIdx(null)}
        />
      )}

      <div className="adm-editor">
        <section className="adm-section">
          <h3 className="adm-section__title">Section Heading</h3>

          <div className="adm-field">
            <label className="adm-label">Heading</label>
            <input className="adm-input" value={draft.heading || ''}
              onChange={e => update({ heading: e.target.value })} />
          </div>

          <div className="adm-field">
            <label className="adm-label">Subtext</label>
            <input className="adm-input" value={draft.subtext || ''}
              onChange={e => update({ subtext: e.target.value })} />
          </div>
        </section>

        <section className="adm-section">
          <h3 className="adm-section__title">Steps</h3>

          <div className="adm-card-list">
            {steps.map((step, i) => (
              <div key={i} className="adm-card-item" draggable
                onDragStart={e => e.dataTransfer.setData('text/plain', i)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); moveStep(+e.dataTransfer.getData('text/plain'), i); }}>

                <div className="adm-card-item__header">
                  <span className="adm-list__drag">⠿</span>
                  <input className="adm-input adm-input--sm" style={{ width: 52 }}
                    placeholder="01" value={step.num || ''}
                    onChange={e => updateStep(i, { num: e.target.value })} />
                  <button className="adm-btn adm-btn--danger adm-btn--sm"
                    onClick={() => setDeleteIdx(i)}>✕</button>
                </div>

                <div className="adm-field">
                  <label className="adm-label">Title</label>
                  <input className="adm-input" value={step.title || ''}
                    onChange={e => updateStep(i, { title: e.target.value })} />
                </div>

                <div className="adm-field">
                  <label className="adm-label">Description</label>
                  <textarea className="adm-textarea" rows={2} value={step.desc || ''}
                    onChange={e => updateStep(i, { desc: e.target.value })} />
                </div>
              </div>
            ))}
          </div>

          <button className="adm-btn adm-btn--ghost" onClick={addStep}>+ Add Step</button>
        </section>
      </div>
    </div>
  );
}
